/**
 * Flow-buffer volume profile strip on the right edge of the chart pane.
 * Directional ▲/▼ markers are drawn via FlowOpportunityMarkersPrimitive on the series.
 */
import React, { useCallback, useEffect, useRef } from "react";
import type { IChartApi, ISeriesApi } from "lightweight-charts";
import type { FlowProfileLevel } from "../../utils/flowVolumeProfile";
import { useChartTheme } from "../../theme/chartTheme";

const VP_WIDTH = 72;
/* Bull/bear/neutral come from the palette (--up/--down/--muted-2); alphas are kept as before. */
const BULL_ALPHA = 0.85;
const BEAR_ALPHA = 0.9;
const NEUT_ALPHA = 0.45;
const POC = "rgba(250, 204, 21, 0.95)";
const KEY_BORDER = "rgba(255, 255, 255, 0.55)";

export interface FlowVolumeProfileOverlayProps {
    chart: IChartApi | null;
    series: ISeriesApi<"Candlestick"> | null;
    levels: FlowProfileLevel[];
    enabled: boolean;
}

const FlowVolumeProfileOverlay: React.FC<FlowVolumeProfileOverlayProps> = ({
    chart,
    series,
    levels,
    enabled,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const t = useChartTheme();

    const paint = useCallback(() => {
        const canvas = canvasRef.current;
        const wrap = wrapRef.current;
        if (!canvas || !wrap || !chart || !series || !enabled) return;

        const plotW = chart.timeScale().width();
        const h = wrap.clientHeight;
        if (plotW < 10 || h < 10) return;

        const canvasW = plotW + VP_WIDTH;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = canvasW * dpr;
        canvas.height = h * dpr;
        canvas.style.width = `${canvasW}px`;
        canvas.style.height = `${h}px`;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, canvasW, h);

        if (!levels.length) return;

        let maxMag = 0;
        for (const lv of levels) {
            maxMag = Math.max(
                maxMag,
                Math.abs(lv.directionScore),
                lv.netPremium,
                lv.totalVolume,
            );
        }
        if (maxMag <= 0) maxMag = 1;

        const vpLeft = plotW;
        ctx.globalAlpha = 0.55;
        ctx.fillStyle = t.background;
        ctx.fillRect(vpLeft, 0, VP_WIDTH, h);
        ctx.globalAlpha = 1;

        for (const lv of levels) {
            const y = series.priceToCoordinate(lv.chartPrice);
            if (y == null || y < 4 || y > h - 4) continue;

            const mag =
                Math.abs(lv.directionScore) +
                lv.netPremium * 0.001 +
                lv.totalVolume;
            const barW = (mag / maxMag) * (VP_WIDTH - 10);
            const barH = Math.max(
                2,
                Math.min(10, h / Math.max(levels.length, 20)),
            );

            let fill = t.muted;
            let alpha = NEUT_ALPHA;
            if (lv.direction === "bullish") { fill = t.up; alpha = BULL_ALPHA; }
            if (lv.direction === "bearish") { fill = t.down; alpha = BEAR_ALPHA; }
            if (lv.isPoc) { fill = POC; alpha = 1; }

            const x0 = vpLeft + VP_WIDTH - 4 - barW;
            ctx.globalAlpha = alpha;
            ctx.fillStyle = fill;
            ctx.fillRect(x0, y - barH / 2, barW, barH);
            ctx.globalAlpha = 1;

            if (lv.isKeyLevel || lv.isPoc) {
                ctx.strokeStyle = lv.isPoc ? POC : KEY_BORDER;
                ctx.lineWidth = lv.isPoc ? 2 : 1;
                ctx.strokeRect(
                    vpLeft,
                    y - barH / 2 - 1,
                    VP_WIDTH - 2,
                    barH + 2,
                );
            }

            if (lv.isPoc || (lv.isKeyLevel && lv.rank < 3)) {
                ctx.font = '9px "IBM Plex Mono", ui-monospace, monospace';
                ctx.fillStyle = lv.isPoc ? "#facc15" : t.text;
                ctx.textAlign = "right";
                ctx.textBaseline = "middle";
                const tag = lv.isPoc
                    ? `POC ${lv.strike.toFixed(0)}`
                    : `${lv.direction === "bullish" ? "▲" : lv.direction === "bearish" ? "▼" : "·"} ${lv.strike.toFixed(0)}`;
                ctx.fillText(tag, vpLeft - 4, y);
            }
        }

        ctx.font = '9px "IBM Plex Mono", ui-monospace, monospace';
        ctx.fillStyle = t.text;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText("Flow VP", vpLeft + 4, 6);
        ctx.fillStyle = t.up;
        ctx.fillText("▲ BC / SP", vpLeft + 4, 18);
        ctx.fillStyle = t.down;
        ctx.fillText("▼ SC / LP", vpLeft + 4, 30);
    }, [chart, series, levels, enabled, t]);

    useEffect(() => {
        if (!chart || !enabled) return;
        const redraw = () => requestAnimationFrame(paint);
        redraw();
        chart.timeScale().subscribeVisibleLogicalRangeChange(redraw);
        const ro = new ResizeObserver(redraw);
        if (wrapRef.current) ro.observe(wrapRef.current);
        return () => {
            chart.timeScale().unsubscribeVisibleLogicalRangeChange(redraw);
            ro.disconnect();
        };
    }, [chart, enabled, paint]);

    useEffect(() => {
        paint();
    }, [paint, levels]);

    if (!enabled || !levels.length) return null;

    const plotW = chart?.timeScale().width() ?? 0;

    return (
        <div
            ref={wrapRef}
            style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: plotW > 0 ? plotW + VP_WIDTH : "100%",
                height: "100%",
                pointerEvents: "none",
                zIndex: 4,
                overflow: "hidden",
            }}
        >
            <canvas ref={canvasRef} style={{ display: "block" }} />
        </div>
    );
};

export default FlowVolumeProfileOverlay;
